import React, { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Avatar,
  AvatarFallback,
  Checkbox,
  Icon,
  InvisibleInput,
} from "@elaniintc/goes-design-system";
import SearchInput from "./search-input";
import { cn } from "@/utils/theme";

export interface ColumnHeader {
  icon: string;
  title: string;
  color?: "neutral" | "info" | "action" | "success" | "danger" | "warning";
  iconSize?: "xs" | "sm" | "lg" | "xl" | "2xl";
  editable?: boolean;
  onTitleChange?: (newTitle: string) => void;
  onTitleBlur?: () => void;
  rightContent?: ReactNode;
  hideSearch?: boolean;
  validationError?: string;
  showValidation?: boolean;
  showManualConfirmation?: boolean;
  manualConfirmation?: boolean;
  onManualConfirmationChange?: (checked: boolean) => void;
}

export interface ThreeColumnLayoutProps {
  leftHeader: ColumnHeader;
  centerHeader: ColumnHeader;
  rightHeader: ColumnHeader;
  leftContent: ReactNode;
  centerContent: ReactNode;
  rightContent: ReactNode;
  className?: string;
  minHeight?: string;
  leftContentWidth?: string;
  disableContent?: boolean;
}

export const ThreeColumnLayout: React.FC<ThreeColumnLayoutProps> = ({
  leftHeader,
  centerHeader,
  rightHeader,
  leftContent,
  centerContent,
  rightContent,
  className = "",
  leftContentWidth = "",
  disableContent = false,
}) => {
  const [centerTitle, setCenterTitle] = useState(centerHeader.title);
  const measureSpan = useRef<HTMLSpanElement>(null);
  const [initialInputWidth, setInitialInputWidth] = useState<number>(160);
  const inputWidth =
    centerTitle.length === 0
      ? 160
      : Math.max(
          160,
          Math.max(
            measureSpan.current?.clientWidth || initialInputWidth + 1,
            160,
          ),
        );

  useEffect(() => {
    if (measureSpan.current) {
      setInitialInputWidth(
        Math.max(160, Math.max(measureSpan.current.clientWidth + 1, 160)),
      );
    }
  }, [measureSpan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const maxLength = 254;
    let validText = null;

    if (value.length > maxLength) {
      validText = value.slice(0, maxLength);
    }

    const allowedPattern = /^[a-zA-Z0-9\s\-_áéíóúÁÉÍÓÚñÑüÜ´¨]*$/;
    const finalValue = validText || value;

    if (allowedPattern.test(finalValue)) {
      setCenterTitle(finalValue);
      centerHeader.onTitleChange?.(finalValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");
    const maxLength = 254;

    let finalText = pastedText;
    if (pastedText.length > maxLength) {
      finalText = pastedText.slice(0, maxLength);
    }

    const filteredText = finalText
      .split("")
      .filter((char) => /[a-zA-Z0-9\s\-_áéíóúÁÉÍÓÚñÑüÜ´¨]/.test(char))
      .join("");

    if (filteredText !== pastedText) {
      e.preventDefault();
      setCenterTitle(filteredText);
      centerHeader.onTitleChange?.(filteredText);
    } else if (pastedText.length > maxLength) {
      e.preventDefault();
      setCenterTitle(filteredText);
      centerHeader.onTitleChange?.(filteredText);
    }
  };

  return (
    <div
      className={`grid grid-rows-[auto_1fr] grid-cols-3 h-full divide-y divide-(--border-neutral) rounded-t-lg overflow-hidden ${className}`}
    >
      {/* PRIMERA FILA: HEADERS */}
      {/* Header Columna 1: Left */}
      <div className="bg-neutral-foreground p-4">
        <div className="flex items-center justify-start gap-2">
          <Avatar size="2xs" shape="square">
            <AvatarFallback
              color={leftHeader.color || "neutral"}
              type="ghost"
              shape="square"
            >
              <Icon type="fas" name={leftHeader.icon} />
            </AvatarFallback>
          </Avatar>
          <h2 className="text-h6 text-heading text-">{leftHeader.title}</h2>
          {!leftHeader.hideSearch && (
            <div className="ml-auto">
              <SearchInput className="h-8" placeholder="Buscar..." />
            </div>
          )}
        </div>
      </div>

      {/* Header Columna 2: Center */}
      <div className="bg-neutral-object p-4 border-x border-neutral">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-start">
            <Avatar size="2xs" shape="square" className="mr-2">
              <AvatarFallback
                color={centerHeader.color || "info"}
                type="ghost"
                shape="square"
              >
                <Icon
                  type="fas"
                  name={centerHeader.icon}
                  size={centerHeader.iconSize || "xs"}
                />
              </AvatarFallback>
            </Avatar>
            {centerHeader.editable ? (
              <>
                <InvisibleInput
                  value={centerTitle}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  onBlur={() => centerHeader.onTitleBlur?.()}
                  maxLength={255}
                  placeholder="Nombre"
                  weight={"h6"}
                  style={{
                    width: `${inputWidth}px`,
                    minWidth: "140px",
                    maxWidth: "100%",
                  }}
                  className={
                    centerHeader.showValidation && centerHeader.validationError
                      ? "text-danger"
                      : ""
                  }
                />
                <span
                  ref={measureSpan}
                  className="opacity-0 absolute -z-10 px-4 border text-h6 text-body-loud"
                >
                  {centerTitle}
                </span>
              </>
            ) : (
              <h2 className="text-h6 text-body-loud">{centerHeader.title}</h2>
            )}
            <div className="flex items-center gap-3 ml-auto">
              {centerHeader.showManualConfirmation !== false && (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <label
                    htmlFor="manual-confirmation-center"
                    className="text-sm text-body cursor-pointer select-none"
                  >
                    Confirmación manual
                  </label>
                  <Checkbox
                    id="manual-confirmation-center"
                    checked={centerHeader.manualConfirmation || false}
                    onChange={() => {
                      const newValue = !centerHeader.manualConfirmation;
                      centerHeader.onManualConfirmationChange?.(newValue);
                    }}
                    className="cursor-pointer"
                  />
                </div>
              )}
              {centerHeader.rightContent}
            </div>
          </div>
          {centerHeader.editable &&
            centerHeader.showValidation &&
            centerHeader.validationError && (
              <div className="flex items-center gap-2 ml-10">
                <span className="text-xs text-danger">
                  {centerHeader.validationError}
                </span>
              </div>
            )}
        </div>
      </div>

      {/* Header Columna 3: Right */}
      <div className="bg-neutral-foreground p-4">
        <div className="flex items-center justify-start gap-2">
          <Avatar size="2xs" shape="square" className="mr-2">
            <AvatarFallback
              color={rightHeader.color || "neutral"}
              type="ghost"
              shape="square"
            >
              <Icon type="fas" name={rightHeader.icon} />
            </AvatarFallback>
          </Avatar>
          <h2 className="text-h6 text-heading">{rightHeader.title}</h2>
        </div>
      </div>

      {/* SEGUNDA FILA: CONTENIDO */}
      {/* Contenido Columna 1: Left */}
      <div
        className={cn("bg-neutral-foreground overflow-auto", leftContentWidth)}
      >
        {leftContent}
      </div>

      {/* Contenido Columna 2: Center */}
      <div className="bg-neutral-object rounded-b-lg h-full max-h-screen overflow-y-auto border-x border-neutral relative">
        <div className="w-full">{centerContent}</div>
        {disableContent && (
          <div className="absolute inset-0 bg-neutral-object-overlay" />
        )}
      </div>

      {/* Contenido Columna 3: Right */}
      <div
        className={cn("bg-neutral-foreground overflow-auto", leftContentWidth)}
      >
        {rightContent}
      </div>
    </div>
  );
};

export default ThreeColumnLayout;
